/**
 * Modale per visualizzare i dettagli completi di un appuntamento (sola lettura)
 */

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Chip,
  Divider,
  Card,
  CardContent
} from '@mui/material';
import {
  Person,
  CalendarMonth,
  Schedule,
  LocalHospital,
  Assignment,
  Info,
  Code,
  PriorityHigh
} from '@mui/icons-material';
import { BookingEvent } from '../../services/calendarService';

interface BookingDetailsModalProps {
  open: boolean;
  booking: BookingEvent | null;
  onClose: () => void;
}

const BookingDetailsModal: React.FC<BookingDetailsModalProps> = ({
  open,
  booking,
  onClose
}) => {
  if (!booking) return null;

  const formatDateTime = (date: Date) => {
    return new Date(date).toLocaleString('it-IT', {
      dateStyle: 'full',
      timeStyle: 'short'
    });
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'CONFIRMED': return 'success';
      case 'CANCELLED': return 'error';
      case 'MODIFIED': return 'warning';
      default: return 'primary';
    }
  };

  const getPriorityColor = (priority?: string) => {
    return priority === 'URGENT' ? 'error' : 'default';
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <CalendarMonth color="primary" />
          <Typography variant="h6">Dettagli Appuntamento</Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={3}>
          {/* Informazioni Paziente */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Person color="primary" />
                  <Typography variant="h6">Paziente</Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="textSecondary">Nome</Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {booking.patientName || 'N/D'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="textSecondary">Codice Fiscale</Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {booking.patientFiscalCode || 'N/D'}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Data e Orario */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Schedule color="primary" />
                  <Typography variant="h6">Data e Orario</Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="textSecondary">Inizio</Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {formatDateTime(booking.start)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="textSecondary">Fine</Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {formatDateTime(booking.end)}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Prestazione */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <LocalHospital color="primary" />
                  <Typography variant="h6">Prestazione</Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="textSecondary">Nome Prestazione</Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {booking.serviceName || 'N/D'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="textSecondary">Codice Prestazione</Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {booking.serviceCode || 'N/D'}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Agenda */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Assignment color="primary" />
                  <Typography variant="h6">Agenda</Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="textSecondary">Nome Agenda</Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {booking.diaryName || 'N/D'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="textSecondary">ID Agenda</Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {booking.diaryId}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Status e Priorità */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Info color="primary" />
                  <Typography variant="h6">Stato e Informazioni</Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={3}>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Codice Prenotazione
                    </Typography>
                    <Chip
                      label={booking.bookingCode || booking.bookingId}
                      size="small"
                      icon={<Code />}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Stato
                    </Typography>
                    <Chip
                      label={booking.status || 'N/D'}
                      color={getStatusColor(booking.status)}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Priorità
                    </Typography>
                    <Chip
                      label={booking.priority || 'NORMAL'}
                      color={getPriorityColor(booking.priority)}
                      size="small"
                      icon={<PriorityHigh />}
                    />
                  </Grid>

                  {/* NRE */}
                  {booking.extendedProps?.service?.prescription?.nre && (
                    <Grid item xs={12} md={3}>
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        NRE
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {booking.extendedProps.service.prescription.nre}
                      </Typography>
                    </Grid>
                  )}

                  {/* Data Prenotazione */}
                  {booking.extendedProps?.entryDateTime && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        Data Prenotazione
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {new Date(booking.extendedProps.entryDateTime).toLocaleString('it-IT', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </Typography>
                    </Grid>
                  )}

                  {/* Presa in carico (se presente admission) */}
                  {booking.extendedProps?.service?.admission && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        Presa in Carico
                      </Typography>
                      <Chip
                        label="Sì"
                        color="success"
                        size="small"
                      />
                      <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                        ID: {booking.extendedProps.service.admission.id}
                      </Typography>
                      <Typography variant="caption" display="block">
                        Codice: {booking.extendedProps.service.admission.code}
                      </Typography>
                    </Grid>
                  )}
                </Grid>

                {booking.notes && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Note
                    </Typography>
                    <Typography variant="body1">
                      {booking.notes}
                    </Typography>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Dati Estesi (se disponibili) */}
          {booking.extendedProps && Object.keys(booking.extendedProps).length > 0 && (
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Dati Completi (JSON)
                  </Typography>
                  <Box
                    sx={{
                      maxHeight: 300,
                      overflow: 'auto',
                      backgroundColor: '#f5f5f5',
                      p: 2,
                      borderRadius: 1,
                      fontFamily: 'monospace',
                      fontSize: '0.875rem'
                    }}
                  >
                    <pre>{JSON.stringify(booking.extendedProps, null, 2)}</pre>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Chiudi
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BookingDetailsModal;
